<template>
    <div class="h-full min-h-60 space-y-5">
        <div class="space-y-4">
            <h2 class="text-xl font-semibold text-center">{{ computedState?.form?.title || header?.text || computedState.event?.name || '' }}</h2>
            <p v-if="computedState?.form?.sub_title" class="text-gray-500 text-center">{{ computedState.form.sub_title }}</p>
        </div>
        <div>
            <div v-if="computedState.form" class="grid h-full">
                <div v-for="(formSection, sectionIndex) of computedState.form.sections" :key="formSection.id" :class="{ 'mt-4': formSection.name }" class="first:mt-0">
                    <hr v-if="sectionIndex > 0 && formSection.id === bulkRowsFirstSectionId" class="border-gray-200 mt-4 mb-6" />
                    <div v-if="collapsibleSections[formSection.id] && !sectionsWithInlineDeleteButton[formSection.id]" class="flex gap-x-2 mb-4">
                        <div>
                            <button
                                type="button"
                                class="bg-primary-700 text-primary-800 bg-opacity-20 min-h-[40px] hover:bg-opacity-30 transition-colors duration-300 rounded-lg py-2 px-3 flex"
                                @click="onToggleExpandSectionButtonClicked(formSection)"
                            >
                                <span class="m-auto" :class="{ 'pr-2': formSection.name }">
                                    <Icon :src="formSection.collapsed ? IconSource.AddAction : IconSource.Minus"></Icon>
                                </span>
                                <span>{{ formSection.name }}</span>
                            </button>
                        </div>
                        <div v-if="dynamicSectionOptions[formSection.id]">
                            <DeleteButton @click="onDeleteSection(formSection)">
                                {{ dynamicSectionOptions[formSection.id]?.deleteSectionButtonLabel }}
                            </DeleteButton>
                        </div>
                    </div>
                    <ExpandCollapseTransition>
                        <div v-if="!formSection.collapsed || sectionsWithInlineDeleteButton[formSection.id]" class="grid gap-y-1 gap-x-2" :class="gridColumnsClass">
                            <h3 v-if="sectionIndex > 0 && formSection.name" class="text-lg font-semibold mt-2 mb-4 text-center col-span-full">{{ formSection.name }}</h3>
                            <DynamicFormElement
                                v-for="(formElement, elementIndex) of formSection.children"
                                :ref="`${formSection.id}_${formElement.id}`"
                                :key="formElement.id"
                                v-model:element="formSection.children[elementIndex]"
                                :precondition-disabled="getPreconditionDisabled(formElement, formSection)"
                                :precondition-data="getPreconditionData(formElement, formSection)"
                                :rules="getRules(formElement)"
                                :details="inputDetails[sectionIndex][elementIndex]"
                                :section-id="formSection.id"
                                :assignment-id="computedState.assignment_id"
                                :excluded-value-ids="valueIdsTakenInSectionCopies[`${formSection.id}.${formElement.id}`]"
                                :allocation="allocations[`${formSection.id}.${formElement.id}`]"
                                :label-offset="elementsWithLabelOffset[`${formSection.id}.${elementIndex}`]"
                                @input="validateForm"
                                @clone-section="onCloneSection"
                                @backend-retrieval="(triggeredElement) => onBackendRetrieval(triggeredElement, formSection)"
                            ></DynamicFormElement>
                            <div v-if="sectionsWithInlineDeleteButton[formSection.id]" class="col-span-1">
                                <DeleteButton class="w-full min-h-[3.25rem]" @click="onDeleteSection(formSection)">
                                    {{ dynamicSectionOptions[formSection.id]?.deleteSectionButtonLabel }}
                                </DeleteButton>
                            </div>
                        </div>
                    </ExpandCollapseTransition>
                </div>
                <div v-if="subassignments" class="mt-2 mb-4">
                    <h3 class="text-lg font-semibold mb-3 w-full break-all">{{ $t('global.forms.steps.dynamicForm.sectionHeadings.subAssignments') }} ({{ subassignments.length }})</h3>
                    <transition-group
                        tag="div"
                        enter-active-class="transition-all duration-300 ease-in"
                        leave-active-class="transition-all duration-300 ease-in"
                        enter-class="transform -translate-y-4 opacity-0"
                        leave-to-class="transform translate-y-4 opacity-0"
                    >
                        <div v-for="subassignment in subassignments" :key="subassignment.id">
                            <button
                                type="button"
                                class="flex w-full items-center justify-between bg-white hover:bg-gray-100 transition-colors border border-gray-200 rounded-xl py-3.5 px-4 space-x-4 flex mt-2 text-left"
                                :class="{ 'opacity-70 cursor-not-allowed hover:bg-white': config.editable === false }"
                                :aria-checked="completedSubassignments.includes(subassignment.id) ? 'true' : 'false'"
                                :aria-labelledby="`subassignment-label-${subassignment.id}`"
                                :disabled="config.editable === false"
                                @keydown.space.prevent="completeSubassignment(subassignment.id)"
                                @keydown.enter.prevent="completeSubassignment(subassignment.id)"
                                @click="completeSubassignment(subassignment.id)"
                            >
                                <div :id="`subassignment-label-${subassignment.id}`">
                                    {{ subassignment.description }}
                                </div>
                                <div
                                    class="flex items-center flex-shrink-0 h-8 w-8 rounded-full"
                                    :class="completedSubassignments.includes(subassignment.id) ? 'bg-green-200 bg-opacity-20' : 'border border-gray-400 bg-white'"
                                >
                                    <Icon class="m-auto h-4 opacity-0 transition-opacity" :src="IconSource.Active" :class="{ 'opacity-100': completedSubassignments.includes(subassignment.id) }"></Icon>
                                </div>
                            </button>
                        </div>
                    </transition-group>
                </div>
                <div v-if="computedAssignment && computedAssignment.is_completed === false && config.editable !== false" class="mt-4">
                    <CustomButton :loading="isCompletingAssignment" :disabled="disableButtons" @click="completeAssignment">
                        {{ $t('global.forms.steps.dynamicForm.completeButtonLabel') }}
                    </CustomButton>
                </div>
                <div v-if="config.deleteButton || config.editable !== false" class="flex space-x-4 justify-end mt-4">
                    <div>
                        <CustomButton :disabled="disableButtons || isCompletingAssignment" color-preset="white" class="px-5" @click="$emit('close')">
                            {{ $t('global.forms.steps.dynamicForm.cancelButtonLabel') }}
                        </CustomButton>
                    </div>
                    <div v-if="config.deleteButton">
                        <DeleteFormConfirmationModal v-model:state="state" :type="formType" :header="header" @delete="$emit('skip')">
                            <template #default="scope">
                                <CustomButton :disabled="disableButtons || isCompletingAssignment" color-preset="warning" class="px-5 whitespace-nowrap" @click="scope.open">
                                    {{ (computedState.form.buttons && computedState.form.buttons.delete) || deleteButtonText }}
                                </CustomButton>
                            </template>
                        </DeleteFormConfirmationModal>
                    </div>
                    <div v-if="config.editable !== false" class="flex-grow">
                        <CustomButton
                            :loading="disableButtons"
                            :disabled="!validForm || isCompletingAssignment"
                            :color-preset="computedAssignment && computedAssignment.is_completed === false ? 'secondary' : 'primary'"
                            class="px-5 max-w-full"
                            @click="onNextButtonClicked"
                        >
                            {{ (computedState.form.buttons && computedState.form.buttons.next) || $t('global.forms.steps.dynamicForm.nextButtonLabel') }}
                        </CustomButton>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { Form } from '@makeabledk/vue-ui/support/http';
import cloneDeep from 'lodash-es/cloneDeep';
import { Form as FormType, FormElement, FormSection, MultiStepFormState, CloneFormSectionOptions, Assignment, IconSource } from '@/types';
import AddButton from '@/components/ui/AddButton.vue';
import DeleteButton from '@/components/ui/DeleteButton.vue';
import CustomButton from '@/components/ui/CustomButton.vue';
import DynamicFormElement from '@/components/common/multiStepFormSteps/DynamicFormElement.vue';
import DeleteFormConfirmationModal from '@/components/common/multiStepFormSteps/DeleteFormConfirmationModal.vue';
import dynamicFormRules from '@/components/common/multiStepFormSteps/useDynamicFormRules';
import { store } from '@/plugins/store';
import { ActionType } from '@/plugins/store/actions';
import ExpandCollapseTransition from '@/components/common/ExpandCollapseTransition.vue';
import { i18n } from '@/plugins/internationalization/i18n';

export default defineComponent({
    components: { AddButton, DeleteButton, DynamicFormElement, CustomButton, DeleteFormConfirmationModal, ExpandCollapseTransition },
    props: {
        state: {
            type: Object as () => MultiStepFormState & Form<MultiStepFormState>,
            required: true,
        },
        config: {
            type: Object as () => { deleteButton?: boolean; editable?: boolean },
            default: null,
        },
        header: {
            type: Object as () => null | { icon_url: string; text?: string },
            default: null,
        },
        assignment: {
            type: Object as () => null | Assignment,
            default: null,
        },
    },
    emits: ['update:state', 'submit', 'skip', 'update:assignment', 'close', 'force-close', 'submission'],
    data() {
        return {
            validForm: false,
            initialFormState: cloneDeep(this.$props.state.form),
            dynamicallyAddedSections: {} as { [key: string]: number },
            collapsibleSections: {} as { [key: string]: boolean },
            validationDebounceTimeout: null as null | NodeJS.Timeout,
            retrievalRequestIds: {} as Record<string, number>,
            disableButtons: false,
            completedSubassignments: [] as number[],
            isCompletingAssignment: false,
            IconSource,
        };
    },
    computed: {
        computedState: {
            get(): MultiStepFormState & Form<MultiStepFormState> {
                return this.$props.state;
            },
            set(newValue: MultiStepFormState) {
                this.$emit('update:state', newValue);
            },
        },
        computedAssignment: {
            get(): Assignment | null {
                return this.$props.assignment;
            },
            set(newValue: Assignment) {
                this.$emit('update:assignment', newValue);
            },
        },
        inputDetails(): ({ type: 'error'; text: string } | undefined)[][] {
            const formHasErrors = this.computedState.errors.hasErrors();
            return (this.computedState.form as FormType).sections.map((formSection: FormSection) =>
                formSection.children.map((formElement) => {
                    const error = formHasErrors ? this.computedState.errors.all().find((currentError) => currentError.id === `${formSection.id}.${formElement.id}`) : undefined;
                    return error ? { type: 'error', text: error.value } : undefined;
                })
            );
        },
        dynamicSectionOptions(): {
            [key: string]: null | {
                deleteSectionButtonLabel: string | undefined;
                prototypeId: string | undefined;
            };
        } {
            return this.computedState.form?.sections.reduce((accum: any, section) => {
                const matches = section.id.match(/(.*)_(\d*)$/);
                if (!(matches && matches.length === 3)) {
                    accum[section.id] = null;
                    return accum;
                }

                const dynamicSectionPrototype: FormSection | undefined = this.computedState.form?.sections.find((currentSection) => currentSection?.id === matches[1]);

                let buttonThatAddedSection: undefined | FormElement;
                let sectionIndex = 0;
                while (!buttonThatAddedSection && sectionIndex < (this.computedState.form?.sections.length || 0)) {
                    buttonThatAddedSection = this.computedState.form?.sections[sectionIndex].children.find(
                        (currentElement) => currentElement.type === 'button' && currentElement.type_settings.action === 'clone-section' && currentElement.type_settings.reference === dynamicSectionPrototype?.id
                    );
                    sectionIndex++;
                }
                const deleteSectionButtonLabel = buttonThatAddedSection?.type_settings.values.find((currentValue) => currentValue.id === 'btn-delete-name').value;

                if (buttonThatAddedSection) {
                    accum[section.id] = {
                        deleteSectionButtonLabel: deleteSectionButtonLabel || '',
                        prototypeId: dynamicSectionPrototype?.id,
                    };
                } else {
                    accum[section.id] = null;
                }
                return accum;
            }, {});
        },
        formType() {
            if (this.computedState.assignment_id !== null && this.computedState.assignment_id !== undefined) {
                return 'ASSIGNMENT';
            }

            if (this.computedState.assignment_template_id !== null && this.computedState.assignment_template_id !== undefined) {
                return 'ASSIGNMENT_TEMPLATE';
            }

            if (this.computedState.note_id !== null && this.computedState.note_id !== undefined) {
                return 'NOTE';
            }

            if (this.computedState.resource) {
                return 'RESOURCE';
            }

            return 'RECORD';
        },
        deleteButtonText() {
            if (this.formType === 'ASSIGNMENT') {
                return i18n.global.t('global.forms.steps.deleteAssignment.deleteRecordButtonLabel');
            }

            if (this.formType === 'ASSIGNMENT_TEMPLATE') {
                return i18n.global.t('global.forms.steps.deleteAssignmentTemplate.deleteRecordButtonLabel');
            }

            return i18n.global.t('global.forms.steps.deleteRecord.deleteRecordButtonLabel');
        },
        /** Every section that can be cloned, with its copies, by the id of the prototype section, e.g. FG_Row: [FG_Row, FG_Row_0, FG_Row_1] */
        sectionCopiesByPrototype(): { [prototypeId: string]: FormSection[] } {
            return (this.computedState.form?.sections || []).reduce((accum: { [prototypeId: string]: FormSection[] }, section) => {
                const prototypeId = this.dynamicSectionOptions[section.id]?.prototypeId || section.id;
                (accum[prototypeId] ||= []).push(section);
                return accum;
            }, {});
        },
        /** In a bulk form, the first section that is cloned into rows, which a separator parts from the common fields above it */
        bulkRowsFirstSectionId(): string | null {
            if (!this.computedState.bulk) {
                return null;
            }
            const sections = this.computedState.form?.sections || [];
            const clonedSectionIds = sections.flatMap((section) =>
                section.children.filter((element) => element.type === 'button' && element.type_settings.action === 'clone-section').map((element) => element.type_settings.reference)
            );
            return sections.find((section) => clonedSectionIds.includes(this.dynamicSectionOptions[section.id]?.prototypeId || section.id))?.id || null;
        },
        /** For a dropdown whose value must be unique across the copies of its section, by `sectionId.elementId`: the values picked in the other copies */
        valueIdsTakenInSectionCopies(): { [sectionElementId: string]: string[] } {
            const taken: { [sectionElementId: string]: string[] } = {};
            Object.values(this.sectionCopiesByPrototype).forEach((copies) =>
                copies.forEach((section) =>
                    section.children
                        .filter((element) => element.type_settings.unique_across_clones)
                        .forEach((element) => {
                            taken[`${section.id}.${element.id}`] = copies
                                .filter((otherSection) => otherSection !== section)
                                .map((otherSection) => otherSection.children.find((otherElement) => otherElement.id === element.id)?.value?.id)
                                .filter((valueId): valueId is string => valueId !== undefined && valueId !== null);
                        })
                )
            );
            return taken;
        },
        /** For an allocation summary, by `sectionId.elementId`: the total, and how much of it the copies of the referenced section allocate */
        allocations(): { [sectionElementId: string]: { allocated: number; total: number | null } } {
            const sections = this.computedState.form?.sections || [];
            const allocations: { [sectionElementId: string]: { allocated: number; total: number | null } } = {};
            sections.forEach((section) =>
                section.children
                    .filter((element) => element.type === 'allocation-summary' && element.type_settings.reference)
                    .forEach((element) => {
                        const totalReference: string | undefined = element.type_settings.values?.find((currentValue) => currentValue.id === 'total')?.value;
                        const summedElementId: string | undefined = element.type_settings.values?.find((currentValue) => currentValue.id === 'sum')?.value;

                        const [totalSectionId, totalElementId] = (totalReference || '').split('.');
                        const total = sections.find((currentSection) => currentSection.id === totalSectionId)?.children.find((currentElement) => currentElement.id === totalElementId)?.value;
                        const allocated = (this.sectionCopiesByPrototype[element.type_settings.reference!] || []).reduce((sum, copy) => {
                            const value = Number(copy.children.find((currentElement) => currentElement.id === summedElementId)?.value);
                            return Number.isFinite(value) ? sum + value : sum;
                        }, 0);

                        allocations[`${section.id}.${element.id}`] = { allocated, total: total === null || total === undefined || total === '' ? null : Number(total) };
                    })
            );
            return allocations;
        },
        /** In a bulk form of 4 or more columns, a copy of a section whose content is one row leaving a column free has its delete button there, and nothing to fold */
        sectionsWithInlineDeleteButton(): { [sectionId: string]: boolean } {
            const columns = this.computedState.form?.columns || 2;
            if (!this.computedState.bulk || columns < 4) {
                return {};
            }
            return (this.computedState.form?.sections || []).reduce((accum: { [sectionId: string]: boolean }, section) => {
                const rows = this.gridRowsBySection[section.id];
                const contentColumns = section.children.reduce((sum, element) => sum + this.widthOf(element, columns), 0);
                accum[section.id] = Boolean(this.dynamicSectionOptions[section.id]) && rows.length === 1 && contentColumns <= columns - 1;
                return accum;
            }, {});
        },
        /**
         * The elements of each section in the rows of its grid, as the grid places them: an element goes on the next row when it
         * starts a new row or does not fit in what is left of the current one
         */
        gridRowsBySection(): { [sectionId: string]: FormElement[][] } {
            const columns = this.computedState.form?.columns || 2;
            return (this.computedState.form?.sections || []).reduce((accum: { [sectionId: string]: FormElement[][] }, section) => {
                const rows: FormElement[][] = [];
                let usedColumns = columns;
                section.children.forEach((element) => {
                    const width = this.widthOf(element, columns);
                    if (element.new_row || usedColumns + width > columns) {
                        rows.push([]);
                        usedColumns = 0;
                    }
                    rows[rows.length - 1].push(element);
                    usedColumns += width;
                });
                accum[section.id] = rows;
                return accum;
            }, {});
        },
        /**
         * For an element, by `sectionId.elementIndex`: whether its grid row holds a radio group with a label above its boxes, so it
         * is pushed down to line up with the boxes. Keyed by index, as buttons of a form can share an id (e.g. `FF_ButtonDynamicData`)
         */
        elementsWithLabelOffset(): { [sectionElementIndex: string]: boolean } {
            const offsets: { [sectionElementIndex: string]: boolean } = {};
            (this.computedState.form?.sections || []).forEach((section) => {
                const sectionId = section.id;
                const rows = this.gridRowsBySection[sectionId] || [];
                const isLabelledRadioGroup = (element: FormElement) => element.type === 'radiobutton' && element.type_settings.input_type === 'keyvaluepair' && Boolean(element.label);
                rows.filter((row) => row.some(isLabelledRadioGroup)).forEach((row) => {
                    row.filter((element) => !isLabelledRadioGroup(element) && !(element.type === 'button' && element.type_settings.action === 'image-upload')).forEach((element) => {
                        offsets[`${sectionId}.${section.children.indexOf(element)}`] = true;
                    });
                });
            });
            return offsets;
        },
        gridColumnsClass(): string {
            // Static class names, so Tailwind keeps them
            const gridColumnsClasses: { [columns: number]: string } = {
                2: 'grid-cols-2',
                3: 'grid-cols-3',
                4: 'grid-cols-4',
                5: 'grid-cols-5',
                6: 'grid-cols-6',
            };
            return gridColumnsClasses[this.computedState.form?.columns || 2] || 'grid-cols-2';
        },
        subassignments() {
            if (!this.computedAssignment?.sub_assignments?.length) {
                return null;
            }

            const filteredAssignments = this.computedAssignment.sub_assignments.filter((subassignment) => subassignment.is_completed !== true);

            return filteredAssignments.length ? filteredAssignments : null;
        },
    },
    created() {
        this.computedState.form?.sections.forEach((currentSection) => {
            const dynamicallyAddedSectionMatches = currentSection.id.match(/(.*)_(\d*)$/);
            if (dynamicallyAddedSectionMatches && dynamicallyAddedSectionMatches.length === 3) {
                this.dynamicallyAddedSections[dynamicallyAddedSectionMatches[1]] = this.dynamicallyAddedSections[dynamicallyAddedSectionMatches[1]]
                    ? Math.max(this.dynamicallyAddedSections[dynamicallyAddedSectionMatches[1]], Number(dynamicallyAddedSectionMatches[2]) + 1)
                    : Number(dynamicallyAddedSectionMatches[2]) + 1;
            } else {
                this.dynamicallyAddedSections[currentSection.id] = 0;
            }

            this.collapsibleSections[currentSection.id] = currentSection.collapsed;
        });
    },
    mounted() {
        this.validateForm();
    },
    methods: {
        /** The columns an element takes up in the grid: an image upload always takes 2, and no element takes more than the form has */
        widthOf(element: FormElement, columns: number): number {
            return Math.min(element.type === 'button' && element.type_settings.action === 'image-upload' ? 2 : element.column_width || 2, columns);
        },
        findChildById(sections: FormSection[], id: string) {
            if (!sections) {
                return null;
            }

            for (const section of sections) {
                for (const child of section.children) {
                    if (child.id === id) {
                        return child;
                    }
                }
            }

            return null;
        },
        async onBackendRetrieval(triggeredElement: FormElement, triggeredElementParentSection: FormSection) {
            // A field being typed into or picked on keeps its enabled state: disabling it mid-edit drops the reader's focus.
            const locksTrigger = triggeredElement.type !== 'textbox' && triggeredElement.type !== 'datepicker';
            const includeSectionValues = triggeredElement.type_settings.include_section_values === true;
            // Only the latest answer per key may be applied. A trigger that posts the whole section's current values is
            // counted per section, so the newest such request supersedes older ones from any trigger in it; any other
            // trigger per field. Cloned sections repeat their children's element ids, so a field is identified by its section as well.
            const retrievalKey = includeSectionValues ? `${triggeredElementParentSection.id}` : `${triggeredElementParentSection.id}.${triggeredElement.id}`;
            const requestId = (this.retrievalRequestIds[retrievalKey] ?? 0) + 1;
            this.retrievalRequestIds[retrievalKey] = requestId;
            if (locksTrigger) {
                triggeredElement.disabled = true;
            }

            const preconditionReferences = triggeredElement.type_settings.preconditions ?? (includeSectionValues ? [] : undefined);
            const preconditionElements = preconditionReferences?.map((currentPrecondition) => {
                // Preconditions can be sent in the section.element format. If not, we assume that the elements will be within the same section as the triggered element
                const preconditionConstituents = currentPrecondition.split('.');
                const section =
                    preconditionConstituents.length > 1 ? this.computedState.form?.sections.find((currentSection) => currentSection.id === preconditionConstituents[0]) : triggeredElementParentSection;
                return { section, element: section?.children.find((currentElement) => currentElement.id === preconditionConstituents[preconditionConstituents.length - 1]) };
            });

            if (!preconditionElements) {
                if (locksTrigger) {
                    triggeredElement.disabled = false;
                }
                return;
            }

            // Opt-in: the backend computes from fields beyond the preconditions (the trigger itself and its siblings), so it gets the whole section.
            if (includeSectionValues) {
                const postedIds = new Set(preconditionElements.map((currentElement) => `${currentElement.section?.id}.${currentElement.element?.id}`));
                triggeredElementParentSection.children.forEach((currentElement) => {
                    if (!postedIds.has(`${triggeredElementParentSection.id}.${currentElement.id}`)) {
                        preconditionElements.push({ section: triggeredElementParentSection, element: currentElement });
                    }
                });
            }

            const responseData: { values: { id: string; value: string | { id: string; value: string }[]; values?: any[] }[]; messages: { id: string; value: string }[] } = (await store
                .dispatch(ActionType.GetDynamicFormData, {
                    form: {
                        route: triggeredElement.type_settings.route!,
                        data: {
                            context: this.computedState.form?.context,
                            values: preconditionElements.map((currentPreconditionElement) => ({
                                id: `${currentPreconditionElement?.section?.id}.${currentPreconditionElement?.element?.id}`,
                                value: currentPreconditionElement?.element?.value,
                            })),
                        },
                    },
                    options: locksTrigger ? undefined : { skipLoadingIndicator: true },
                })
                .catch((error) => {
                    triggeredElement.disabled = false;
                })) as { values: { id: string; value: string }[]; messages: { id: string; value: string }[] };

            // Failed, or superseded by a newer retrieval for the same key (typing outpaces the backend): the answer is dropped.
            if (!responseData || this.retrievalRequestIds[retrievalKey] !== requestId) {
                // Superseded per section, the newer request may come from another field and will never unlock this one.
                if (includeSectionValues && locksTrigger) {
                    triggeredElement.disabled = false;
                }
                return;
            }

            responseData.values.forEach((currentResponseValue) => {
                const currentResponseElementIdConstituents = currentResponseValue.id.split('.');
                const section =
                    currentResponseElementIdConstituents.length > 1
                        ? this.computedState.form?.sections.find((currentSection) => currentSection.id === currentResponseElementIdConstituents[0])
                        : triggeredElementParentSection;
                const elementId = currentResponseElementIdConstituents[currentResponseElementIdConstituents.length - 1];
                // The options the value must be among: the ones sent, or else the ones the form came with
                const options: { id: string }[] | undefined = Array.isArray(currentResponseValue.values)
                    ? currentResponseValue.values
                    : this.findChildById(this.initialFormState!.sections, elementId)?.type_settings?.values;

                // A value for another section than the one triggering it - e.g. the options of every row following a shared field -
                // applies to all the copies of that section, and to the copies added later
                const appliesToCopies = section !== undefined && section !== triggeredElementParentSection;
                const sections = appliesToCopies ? this.sectionCopiesByPrototype[section.id] || [section] : [section];
                if (appliesToCopies && Array.isArray(currentResponseValue.values)) {
                    const template = this.initialFormState?.sections.find((currentSection) => currentSection.id === section.id)?.children.find((currentElement) => currentElement.id === elementId);
                    if (template) {
                        template.type_settings.values = [...currentResponseValue.values];
                        template.value = currentResponseValue.value;
                    }
                }

                sections.forEach((currentSection) => {
                    const element = currentSection?.children.find((currentElement) => currentElement.id === elementId);
                    if (!element) {
                        return;
                    }

                    const previousValue = element.value;
                    element.value = currentResponseValue.value;

                    const isKeyValue = ['keyvaluepair', 'keyvaluepairlist'].includes(element.type_settings?.input_type);
                    if (!isKeyValue || !options || (!Array.isArray(currentResponseValue.values) && !options.length)) {
                        return;
                    }

                    element.type_settings.values = [...options];
                    // Each cloned row has its own choice. Keep it when the refreshed options still allow it.
                    const selectedValue = appliesToCopies && previousValue !== null && previousValue !== undefined ? previousValue : currentResponseValue.value;
                    if (Array.isArray(selectedValue)) {
                        element.value = (selectedValue as { id: string; value: string }[]).filter((currentValue) => options.some((v) => v.id === currentValue.id));
                    } else if (appliesToCopies && options.some((v) => v.id === selectedValue?.id)) {
                        element.value = selectedValue;
                    } else if (!options.some((v) => v.id === element.value?.id)) {
                        element.value = null;
                    }
                });
            });

            this.computedState.setErrors(responseData.messages);
            this.$nextTick(() => {
                this.validateForm();
            });

            triggeredElement.disabled = false;
        },
        onDeleteSection(formSection: FormSection) {
            const sectionIndex = this.computedState.form!.sections.findIndex((currentSection) => currentSection.id === formSection.id);
            const prototypeId = this.dynamicSectionOptions[formSection.id]?.prototypeId;
            if (sectionIndex < 0 || !prototypeId) {
                return;
            }
            this.dynamicallyAddedSections[prototypeId]--;
            this.computedState.form!.sections.splice(sectionIndex, 1);
            this.$nextTick(() => {
                this.validateForm();
            });
        },
        onCloneSection(cloneFormSectionOptions: CloneFormSectionOptions) {
            const sectionIndex = this.initialFormState!.sections.findIndex((currentSection) => currentSection.id === cloneFormSectionOptions.sectionReference);
            if (sectionIndex < 0) {
                return;
            }
            const clonedSection = cloneDeep(this.initialFormState!.sections[sectionIndex]);
            this.dynamicallyAddedSections[clonedSection.id] = this.dynamicallyAddedSections[clonedSection.id] === undefined ? 1 : this.dynamicallyAddedSections[clonedSection.id] + 1;
            clonedSection.id = `${clonedSection.id}_${this.dynamicallyAddedSections[clonedSection.id] - 1}`;

            clonedSection.collapsed = clonedSection.copy_collapsed;
            this.collapsibleSections[clonedSection.id] = true;

            clonedSection.children.forEach((currentElement) => {
                currentElement.disabled = currentElement.type_settings.copy_locked ? currentElement.disabled : false;
                // A value that must be unique across the copies can't be inherited from the prototype
                if (currentElement.type_settings.unique_across_clones) {
                    currentElement.value = null;
                }
            });
            this.computedState.form!.sections.splice(sectionIndex + this.dynamicallyAddedSections[this.computedState.form!.sections[sectionIndex].id], 0, clonedSection);
            this.$nextTick(() => {
                this.validateForm();
            });
        },
        onNextButtonClicked() {
            if (this.validForm && this.config.editable !== false && !this.disableButtons) {
                this.disableButtons = true;
                this.$emit('submit');
            }
        },
        getRules: dynamicFormRules,
        getPreconditionDisabled(formElement: FormElement, formSection: FormSection): boolean {
            return Boolean(
                formElement.type_settings.preconditions &&
                    formSection.children.find(
                        (currentFormElement) =>
                            !this.getRules(currentFormElement).every((rule) => rule.validate(currentFormElement.value)) && (formElement.type_settings.preconditions || []).includes(currentFormElement.id)
                    )
            );
        },
        getPreconditionData(formElement: FormElement, formSection: FormSection) {
            return formElement.type_settings.preconditions?.map((currentPrecondition) => {
                const preconditionElementReference = formSection.children.find((currentFormElement) => {
                    const currentPreconditionConstituents = currentPrecondition.split('.');
                    return currentFormElement.id === currentPreconditionConstituents[currentPreconditionConstituents.length - 1];
                });

                return { label: preconditionElementReference?.label, value: preconditionElementReference?.value, id: preconditionElementReference?.id };
            });
        },
        onToggleExpandSectionButtonClicked(formSection: FormSection) {
            formSection.collapsed = !formSection.collapsed;
        },
        validateForm() {
            if (this.validationDebounceTimeout) {
                clearTimeout(this.validationDebounceTimeout);
            }

            this.validationDebounceTimeout = setTimeout(() => {
                const validationResult = Object.values(this.$refs)
                    .filter((currentRef: any) => currentRef)
                    .every((currentRef: any) => currentRef?.[0]?.validInput ?? true);
                this.validForm = validationResult;
            }, 150);
        },
        async completeAssignment() {
            if (!this.computedAssignment) {
                return;
            }

            try {
                if (this.isCompletingAssignment) return;

                this.isCompletingAssignment = true;

                await store.dispatch(ActionType.EditAssignment, { form: { assignmentId: this.computedAssignment.id, payload: { ...this.computedAssignment, is_completed: true } } });

                this.$emit('submission');
                this.$emit('force-close');
            } catch (err) {
                this.isCompletingAssignment = false;
            }
        },
        async completeSubassignment(subassignmentId: number) {
            if (!this.computedAssignment?.sub_assignments) {
                return;
            }

            try {
                if (this.completedSubassignments.includes(subassignmentId)) return;

                this.completedSubassignments.push(subassignmentId);

                await store.dispatch(ActionType.EditSubassignment, { form: { assignmentId: this.computedAssignment.id, subId: subassignmentId, payload: { is_completed: true } } });

                const index = this.computedAssignment.sub_assignments.findIndex((s) => s.id === subassignmentId);

                // Fake delay for better transition
                // eslint-disable-next-line
                await new Promise<void>((resolve) => setTimeout(() => resolve, 200));

                if (index !== -1) {
                    (this.computedAssignment as Assignment).sub_assignments[index].is_completed = true;

                    if (this.computedAssignment.sub_assignments.every((s) => s.is_completed)) {
                        this.$emit('submission');
                        this.$emit('force-close');
                    }
                }
            } catch (err) {
                this.completedSubassignments = this.completedSubassignments.filter((s) => s !== subassignmentId);
            }
        },
    },
});
</script>
